import { Company, CompanyType, getCompanySuffix } from "@/features/types";
import useListAllCompanies, {
  ListAllCompaniesPathParameter,
  ListAllCompaniesRequest,
} from "@/features/line-sheet-sets/hooks/use-list-all-companies";
import useIdentity from "@/features/ui/hooks/use-identity";
import AppSelect from "@/features/line-sheet-sets/app-select";
import { useMemo } from "react";
import useI18nHelper from "@/features/ui/hooks/use-i18n-helper";
import useAppToasts from "@/features/line-sheet-sets/hooks/use-app-toasts";
import { Deserializable, Serializable } from "@/features/ui/app.type";
import { boolean, string } from "yup";

export class CompanyId extends Deserializable implements Serializable {
  private readonly _id: number;
  private readonly _type: CompanyType;

  constructor(id: number, type: CompanyType) {
    super();
    this._id = id;
    this._type = type;
  }

  get id() {
    return this._id;
  }

  get type() {
    return this._type;
  }

  serialize(): string {
    return `${this._id}:${this._type}`;
  }

  isDeserializable(str: string): boolean {
    return /^[0-9]+:[A-Z_]+$/.test(str);
  }
}

function isCompanyId(candidate: any): candidate is CompanyId {
  return candidate && candidate.id && candidate.type;
}

function isCompanyIdList(candidate: any): candidate is CompanyId[] {
  return Array.isArray(candidate) && candidate.every(isCompanyId);
}

interface BaseCompanySelectProps {
  name: string;
  placeholderText?: string;
  types: CompanyType[];
  // 목록 정렬. "recent_orders" 면 최근 3개월 주문 많은 회사부터 보여준다.
  orderBy?: ListAllCompaniesRequest["orderBy"];
  width?: string;
  height?: string;
  minWidth?: string;
  isNullable?: boolean;
  excludeIds?: number[];
  isDisabled?: boolean;
  isClearable?: boolean;
  isSearchable?: boolean;
  isReadOnly?: boolean;
  isLiteral?: boolean;
  isMultiple?: boolean;
  requestComponent?: React.ReactNode;
  customOptionList?: {
    name: string;
    value: Company;
  }[];
  outerOpen?: boolean;
  setOuterOpen?: (open: boolean) => void;
  customText?: string;
  customTrigger?: React.ReactNode;
  isChart?: boolean;
  chartBrandList?: any[];
  customDataList?: any[];
  variant?: "normal" | "icon" | "chip";
  showCount?: boolean;
  showSelectedBorder?: boolean;
  showEmptyDataOption?: boolean;
}

interface SingleCompanySelectProps extends BaseCompanySelectProps {
  id?: number | CompanyId;
  isMultiple?: false;
  onSelect: (value?: Company) => void;
}

interface MultipleCompanySelectProps extends BaseCompanySelectProps {
  id?: number[] | CompanyId[];
  isMultiple: true;
  onSelect: (value?: Company[]) => void;
}

type CompanySelectProps = SingleCompanySelectProps | MultipleCompanySelectProps;

export function getSelectedCompanyValue(
  data?: Company[],
  id?: CompanySelectProps["id"],
  isChart: boolean = false,
  customData?: any[]
) {
  let predicate: ((company: Company) => boolean) | undefined;
  if (data && id) {
    if (Array.isArray(id)) {
      if (isCompanyIdList(id)) {
        predicate = (company) =>
          id.some(
            (candidate) =>
              candidate.id === company.id && candidate.type === company.type
          );
      } else {
        const numberedIds: number[] = id.map((item) => Number(item));
        predicate = (company) => numberedIds.includes(company.id);
      }
      if (isChart)
        return id
          .map((i) => customData?.find((company) => company.id === Number(i)))
          .filter((c): c is Company => Boolean(c));

      // id 순서로 sort
      return data.filter(predicate).sort((a, b) => {
        const indexA = id.findIndex((i) =>
          isCompanyId(i)
            ? i.id === a.id && i.type === a.type
            : Number(i) === a.id
        );
        const indexB = id.findIndex((i) =>
          isCompanyId(i)
            ? i.id === b.id && i.type === b.type
            : Number(i) === b.id
        );
        return indexA - indexB;
      });
    } else {
      if (isCompanyId(id)) {
        predicate = (company) =>
          company.id === id.id && company.type === id.type;
      } else {
        if (customData) {
          return customData.find((company) => company.id === Number(id));
        }

        predicate = (company) => company.id === id;
      }

      return data.find(predicate);
    }
  }
}

export function withSelectCompanyIdFunc(types?: CompanyType[]) {
  if (!types || types.length > 1) {
    return (company: Company) => `${company.id}__${company.type}`;
  }
  return (company: Company) => company.id;
}

export default function AppCompanySelect({
  types,
  orderBy,
  id,
  onSelect,
  width = "144px",
  height,
  name,
  placeholderText = "",
  isMultiple = false,
  isNullable = false,
  excludeIds = [],
  isDisabled = false,
  isClearable,
  isSearchable = true,
  isReadOnly = false,
  isLiteral = false,
  requestComponent,
  customOptionList,
  outerOpen,
  setOuterOpen,
  customText,
  customTrigger,
  isChart,
  chartBrandList,
  customDataList,
  variant,
  ...props
}: CompanySelectProps) {
  const identity = useIdentity();
  const { error: showError } = useAppToasts({ id: "GLOBAL" });
  const key = useMemo(():
    | {
        parameter: ListAllCompaniesPathParameter;
        request: ListAllCompaniesRequest;
      }
    | undefined => {
    if (identity?.company) {
      let request: ListAllCompaniesRequest = {};
      if (types) {
        request.type__in = types;
      }
      if (orderBy) {
        request.orderBy = orderBy;
      }
      return {
        parameter: {
          by: identity.company.type,
          companyId: identity.company.id,
        },
        request,
      };
    }
    return undefined;
  }, [identity, types, orderBy]);

  let { isLoading, data, mutate, error } = useListAllCompanies(
    key?.parameter,
    key?.request
  );


  const { t } = useI18nHelper();

  // let companies: AppSelectOption<Company>[] = [];
  // if (!isLoading && data) {
  //   if (isNullable) {
  //     data = [
  //       {
  //         id: -1,
  //         name: t("unassigned"),
  //         types: "BOUTIQUE" as CompanyType,
  //       },
  //     ].concat(data);
  //   }
  //   companies = data
  //     .filter((c) => !excludeIds?.includes(c.id))
  //     .map(
  //       (c): AppSelectOption<Company> => ({
  //         name: c.name,
  //         value: c,
  //       })
  //     );
  // }

  const options = useMemo(() => {
    if (data) {
      const addSuffix =
        process.env.NODE_ENV === "development" && (!types || types.length > 1);

      const items = data.filter((c) => !excludeIds?.includes(c.id));

      return items.map((item, index) => {
        let suffix = "";
        if (addSuffix) {
          const previousItem = index > 0 ? items[index - 1] : null;
          const nextItem = index < items.length - 1 ? items[index + 1] : null;

          if (
            (previousItem && previousItem.name === item.name) ||
            (nextItem && nextItem.name === item.name) ||
            true
          ) {
            suffix = `(${item.type ? getCompanySuffix(item.type) : ""})`;
          }
        }
        return {
          name:
            identity?.company?.type === "AGENCY" && item.type === "BOUTIQUE"
              ? `[${item.name}] ${item.orgName}${suffix}`
              : `${item.name}${suffix}`,
          value: item,
          // 브랜드 유사 표기로도 검색되게 한다(화면에는 이름만 보인다).
          keywords: item.similarWords,
        };
      });
    }
    return [];
  }, [data, types, excludeIds, identity?.company?.type]);

  const idFunc = useMemo(() => withSelectCompanyIdFunc(types), [types]);

  const appSelectProps = {
    name,
    placeholderText,
    options: customOptionList ?? options,
    idFunc,
    isSearchable,
    width,
    height,
    isDisabled,
    isClearable,
    isReadOnly,
    isLiteral,
  };

  if (isMultiple) {
    return (
      <AppSelect<Company>
        {...appSelectProps}
        onSelect={onSelect as (value?: Company[]) => void}
        value={
          getSelectedCompanyValue(
            data,
            id,
            isChart,
            chartBrandList ?? customDataList
          ) as Company[]
        }
        isMultiple={true}
        requestComponent={requestComponent}
        outerOpen={outerOpen}
        setOuterOpen={setOuterOpen}
        customText={customText}
        customTrigger={customTrigger}
        variant={variant}
        {...props}
      />
    );
  } else {
    return (
      <AppSelect<Company>
        {...appSelectProps}
        onSelect={onSelect as (value?: Company) => void}
        isMultiple={false}
        requestComponent={requestComponent}
        value={
          getSelectedCompanyValue(
            data,
            id,
            undefined,
            customDataList
          ) as Company
        }
      />
    );
  }
}
